import { FC, useMemo } from 'react';
import { Modal } from 'react-bootstrap';

import { useTranslate } from 'hooks/useTranslate';

import encumbrancesExcelModalSteps from '../constants/encumbrancesExcelModalSteps';
import {
  getEncumbrancesExcelImportSummary,
  isBlockingGroup,
} from '../utils/encumbrancesExcelImportHelpers';
import { useEncumbrancesExcelModal } from '../context/EncumbrancesExcelModalContext';

const EncumbrancesExcelModalFooter: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useEncumbrancesExcelModal();

  const isPreview = data.step === encumbrancesExcelModalSteps.PREVIEW;

  const importSummary = useMemo(() => {
    if (!data.excelPreview) {
      return null;
    }

    return getEncumbrancesExcelImportSummary({
      groups: data.excelPreview.groups,
      uploadInformationalWithErrors: data.uploadInformationalWithErrors,
    });
  }, [data.excelPreview, data.uploadInformationalWithErrors]);

  if (!isPreview) {
    return null;
  }

  const hasBlockingGroups = (importSummary?.blockingCount || 0) > 0;
  const canImport = (importSummary?.importableCount || 0) > 0 && !hasBlockingGroups;

  return (
    <Modal.Footer>
      <div className="d-flex justify-content-between align-items-center w-100">
        <div>
          <button
            type="button"
            className="btn btn-lg btn-outline-light"
            onClick={actions.handleBackToUpload}
            disabled={data.isLoading || data.isParsingExcel}
            data-testid="encumbrances-excel-back-button"
          >
            {t('BackToUpload')}
          </button>

          {hasBlockingGroups && (
            <span
              className="text-soft small ml-2"
              data-testid="encumbrances-excel-preview-error-hint"
            >
              {t('EncumbrancesExcelCorrectErrorsToContinue')}
            </span>
          )}
        </div>

        <button
          type="button"
          className="btn btn-lg btn-primary"
          onClick={actions.handleImport}
          disabled={
            !canImport || data.isLoading || data.isParsingExcel
          }
          data-testid="encumbrances-excel-import-button"
        >
          {t('EncumbrancesExcelImport')}
        </button>
      </div>
    </Modal.Footer>
  );
};

export default EncumbrancesExcelModalFooter;
